// Types for the DD Property Listings API response

export interface DDMediaItem {
  id: number;
  sortOrder: number;
  statusCode: string;
  caption: string | null;
  type: string;
  thumbnailUrl: string | null;
  mimeType: string;
  urlTemplate: string;
  isCover?: boolean;
}

export interface DDFloorPlan {
  id: number;
  sortOrder: number;
  statusCode: string;
  caption: string | null;
  type: string;
  thumbnailUrl: string | null;
  mimeType: string;
  urlTemplate: string;
}

export interface DDUnitDetails {
  configuration: DDConfiguration;
  furnishing: DDFurnishing | null;
  features: DDFeature[];
  dimensions: DDDimension;
}

export interface DDUnitTypeMedia {
  unitTypeId: number;
  floorPlans: DDFloorPlan[];
}

export interface DDMedia {
  cover: DDMediaItem[];
  listingImages: DDMediaItem[];
  unitTypes: {
    listing: DDUnitTypeMedia[];
    project: DDUnitTypeMedia[];
  };
  agencyLogos: DDMediaItem[];
  agent: DDMediaItem[];
}

export interface DDConfiguration {
  bedrooms: { text: string; value: number };
  bathrooms: { text: string; value: number };
  extrarooms: { text: string; value: null | number };
}

export interface DDDimension {
  floor: {
    size: Array<{ formatted: string; uom: string; value: number }>;
  };
  land: {
    size: Array<{ formatted: string; uom: string; value: unknown }>;
  };
}

export interface DDFeature {
  code: string;
  description: string;
}

export interface DDFurnishing {
  code: string | null;
  description: string;
}

export interface DDPricePerArea {
  unit: string;
  text: string;
  value: number;
}

export interface DDUnitType {
  id: number; // used for URL hash fragment
  unitTypeId: number; // used to match floor plans in media
  statusCode: string;
  name: string;
  configuration: DDConfiguration;
  dimensions: {
    floor: Array<{
      size: {
        uom: string;
        min: { formatted: string; value: number } | null;
        max: null;
      };
    }>;
    land: Array<{ size: { uom: string; min: null; max: null } }>;
  };
  minPrice: { text: string; value: number } | null;
  maxPrice: { text: string; value: number } | null;
  minPricePerArea: DDPricePerArea[];
  maxPricePerArea: DDPricePerArea[] | null;
  priceType: { code: string; text: string };
  furnishing: DDFeature[];
  features: DDFeature[];
  facilities: DDFeature[];
}

export interface DDListingUrls {
  listing: {
    desktop: string;
    mobile: string;
    desktopByLocales: {
      en: string;
      th: string;
    };
  };
}

export interface DDListerAgent {
  id: number;
  name: string;
  isVerified: boolean;
  contacts: Array<{ type: string; value: string; pretty: string }>;
}

export interface DDListerDeveloper {
  id: number;
  name: string;
  isVerified: boolean;
  contacts: Array<{ type: string; value: string; pretty: string }>;
}

export interface DDLister {
  type: 'agent' | 'developer';
  metaByType: {
    agent?: DDListerAgent;
    developer?: DDListerDeveloper;
  };
}

export interface DDListingType {
  code: string;
  text: string;
  subTypeCode: string | null; // 'PROJ' for developer/project listings
}

export interface DDPrice {
  min: number | null;
  max: number | null;
  formatted: string | null;
  currency: string;
  perArea: {
    floor: Array<{
      uom: string;
      min: number | null;
      max: number | null;
      text: string | null;
    }>;
  };
}

export interface DDLocationLevel {
  levelName: string;
  level: number;
  id: string;
  value: string;
  slug: string;
}

export interface DDLocation {
  address: {
    formatted: string;
    postalCode: string | null;
  };
  levels: DDLocationLevel[];
}

export interface DDProjectMeta {
  id: string;
  name: string;
  developer: string;
  property: {
    type: { code: string; text: string };
    subType: { code: string; text: string };
  };
  completionYear: number | null;
  totalUnits: number | null;
}

export interface DDProject {
  type: string;
  metaByType: {
    verified?: DDProjectMeta;
  };
}

export interface DDListingsResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  listings: DDListing[];
}

export interface DDMedia {
  cover: DDMediaItem[];
  listingImages: DDMediaItem[];
  unitTypes: {
    listing: DDUnitTypeMedia[];
    project: DDUnitTypeMedia[];
  };
  agencyLogos: DDMediaItem[];
  agent: DDMediaItem[];
  developerLogos: DDMediaItem[];
  projectLogos: DDMediaItem[]; // ← add this
}
export interface DDDeveloperListing extends DDListing {
  listingType: DDListingType & { subTypeCode: 'PROJ' };
}

export interface DDAgentListing extends DDListing {
  lister: DDLister & { type: 'agent' };
}
export interface DDDates {
  contentUpdated: { date: string; timestamp: number };
}
export interface DDListing {
  id: number;
  statusCode: string;
  listingType: DDListingType;
  title: { en: string; th: string };
  headlines: Array<{ text: string; locale: string; brand: string }>;
  urls: DDListingUrls;
  lister: DDLister;
  location: DDLocation;
  media: DDMedia;
  price: DDPrice;
  project: DDProject;
  unitTypes: DDUnitType[];
  unitDetails: DDUnitDetails | null;
  dates: DDDates;
}

// Helper to resolve pgimgs CDN URL template
// urlTemplate contains "${viewType}" placeholder e.g.
// "https://th1-cdn.pgimgs.com/listing/123/UPHO.456.${viewType}/filename.jpg"
export const resolvePGImageUrl = (
  urlTemplate: string,
  viewType = 'V550',
): string => {
  return urlTemplate.replace('${viewType}', viewType);
};

// UTM params appended to all outbound DD Property links
export const DD_UTM_PARAMS =
  'utm_source=tol&utm_medium=referral&utm_campaign=dd_listing';

export const buildDDUrl = (baseUrl: string, hash?: string): string => {
  const urlWithUtm = `${baseUrl}?${DD_UTM_PARAMS}`;
  return hash ? `${urlWithUtm}#${hash}` : urlWithUtm;
};
